import React from 'react'
import { observer } from 'mobx-react'

import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeaderCloseButton,
  ModalSheetHeaderShell,
} from 'components/ModalSheet'
import Button from 'components/Button'
import ContentLoading from 'components/ContentLoading'
import AccessDeniedView from 'components/AccessDeniedView'
import ContentError from 'components/ContentError'
import { Forbidden } from 'fejl'
import JobCreationQuestions from './JobCreationQuestions'
import JobCreationBilling from './JobCreationBilling'
import CreateJobDialogViewModel from './JobCreationDialogViewModel'
import styles from './JobCreationDialog.sass'
import { match } from 'utils/pattern-match-util'
import Icon from 'components/Icon'
import { pluralize } from 'utils/pluralization-util'

interface CreateJobDialogProps {
  vm: CreateJobDialogViewModel
}

export default observer(function CreateJobDialog({ vm }: CreateJobDialogProps) {
  return (
    <ModalSheet
      showing={vm.showing}
      disableCloseOnEsc
      disableCloseOnOutsideClick
      onRequestClose={vm.close}
    >
      <ModalSheetHeader vm={vm}></ModalSheetHeader>

      <ModalSheetContent>
        <ModalSheetContentBlock dataTest="job-creation-dialog">
          {vm.edit.match({
            pending: () => <ContentLoading />,
            rejected: (err) =>
              err instanceof Forbidden ? (
                <AccessDeniedView />
              ) : (
                <ContentError large error={err} />
              ),
            resolved: (editor) =>
              vm.jobCreationRootVM.canViewEditor ? (
                match(vm.modalStep, {
                  Questions: () => (
                    <JobCreationQuestions
                      vm={editor}
                      jobWasCreated={vm.jobWasCreated}
                      modalType={vm.modalType}
                    />
                  ),
                  Billing: () => <JobCreationBilling vm={editor} />,
                })
              ) : (
                <AccessDeniedView />
              ),
          })}
        </ModalSheetContentBlock>
      </ModalSheetContent>

      <ModalSheetFooter>
        {match(vm.modalStep, {
          Questions: () => <QuestionsUnansweredFeedback vm={vm} />,
          Billing: () => null,
        })}
        <ModalSheetFooterSpacer />

        {match(vm.modalStep, {
          Questions: () => <QuestionsStepButtonGroup vm={vm} />,
          Billing: () => <BillingStepButtonGroup vm={vm} />,
        })}
      </ModalSheetFooter>
    </ModalSheet>
  )
})

const ModalSheetHeader = observer(function ModalSheetHeader({
  vm,
}: CreateJobDialogProps) {
  const header = vm.modalHeader

  return (
    <ModalSheetHeaderShell size="auto">
      <div className={styles.headerShell}>
        {header.pager && <div className={styles.pager}>{header.pager}</div>}

        <ModalSheetContentTitle dataTest={`draft-title-${vm.draftTitle}`}>
          <div className={styles.headerContent}>{header.title}</div>
        </ModalSheetContentTitle>

        <div className={styles.contentDescription}>{header.description} </div>
      </div>

      <div className={styles.closeButtonContainer}>
        <ModalSheetHeaderCloseButton />
      </div>
    </ModalSheetHeaderShell>
  )
})

const BillingStepButtonGroup = observer(function BillingStepButtonGroup({
  vm,
}: CreateJobDialogProps) {
  return (
    <ModalSheetFooterButtonGroup>
      <Button
        onClick={() => vm.setStep('Questions')}
        type="default"
        dataTest="job-creation-dialog-go-to-questions-step-button"
      >
        Back
      </Button>

      {vm.canViewDetail ? (
        <Button
          type="primary"
          onClick={() => vm.goDetail()}
          dataTest="job-creation-dialog-go-to-detail-button"
        >
          View Draft
        </Button>
      ) : (
        <Button type="primary" onClick={vm.close}>
          Done
        </Button>
      )}
    </ModalSheetFooterButtonGroup>
  )
})

const QuestionsStepButtonGroup = observer(function QuestionsStepButtonGroup({
  vm,
}: CreateJobDialogProps) {
  return (
    <ModalSheetFooterButtonGroup>
      <Button
        onClick={vm.close}
        type="default"
        dataTest="job-creation-dialog-close-button"
      >
        Close
      </Button>

      {vm.isBillingRelevant && (
        <Button
          onClick={() => vm.setStep('Billing')}
          type="primary"
          disabled={!vm.isBillingStepReady}
          dataTest="job-creation-dialog-go-to-billing-step-button"
        >
          Next
        </Button>
      )}

      {vm.canViewDetail && !vm.isBillingRelevant && (
        <Button
          type="primary"
          onClick={() => vm.goDetail()}
          dataTest="job-creation-dialog-go-to-detail-button"
        >
          View Draft
        </Button>
      )}
    </ModalSheetFooterButtonGroup>
  )
})

const QuestionsUnansweredFeedback = observer(
  function QuestionsUnansweredFeedback({ vm }: CreateJobDialogProps) {
    const count = vm.incompleteQuestionsCount
    return (
      <ModalSheetFooterButtonGroup>
        {vm.modalType === 'EditDraft' && count > 0 && (
          <div className={styles.unansweredFeedback}>
            <div className={styles.unansweredFeedbackIcon}>
              <Icon size={17} name="info" />
            </div>
            <div className={styles.unansweredFeedbackText}>
              {`${count} required ${pluralize('answer', count)} pending`}
            </div>
          </div>
        )}
      </ModalSheetFooterButtonGroup>
    )
  }
)
